import { ChangeEvent, ReactNode } from "react";
import { ViewItem, viewItemProps } from "../../../../Components/Views/ViewItem";
import { controllers } from "../../../../Controllers/Controllers";
import { navigator } from "../../../../Controllers/Navigator";
import { LEAD_STATUS_OPTIONS, LeadCategoryType, LeadFormFields, emptyLeadFormFields } from "../../../../DataTypes/leads";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import './lead-form-styles.css'

const OUTREACH_METHOD_OPTIONS: [string, string][] = [
    ['', 'Not set'],
    ['site_form', 'Website Contact Form'],
    ['email', 'Email'],
    ['linkedin', 'LinkedIn Message'],
    ['phone', 'Phone Call'],
    ['other', 'Other'],
]

type LeadFormPageProps = viewItemProps & {
    leadId?: string;
}

type LeadFormPageState = {
    fields: LeadFormFields;
    categories: LeadCategoryType[];
    loading: boolean;
    needsAuth: boolean;
    loadError: string | null;
    saving: boolean;
    saveError: string | null;
}

class LeadFormPage extends ViewItem {
    props: LeadFormPageProps;
    state: LeadFormPageState;

    constructor(props: LeadFormPageProps) {
        super(props)
        this.props = props
        this.state = {
            fields: { ...emptyLeadFormFields },
            categories: [],
            loading: true,
            needsAuth: false,
            loadError: null,
            saving: false,
            saveError: null,
        }
    }

    get isEditMode(): boolean {
        return !!this.props.leadId
    }

    componentDidMount(): void {
        this.loadInitialData()
    }

    loadInitialData = async () => {
        this.setState({ loading: true, loadError: null, needsAuth: false })

        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }

        const categoriesResponse = await controllers().outreach.getCategories()
        const categories = (categoriesResponse && categoriesResponse.statusCode === 200)
            ? (categoriesResponse.data?.categories || [])
            : []

        if (!this.props.leadId) {
            this.setState({ loading: false, categories })
            return
        }

        const leadResponse = await controllers().outreach.getLeadDetail(Number(this.props.leadId))
        if (!leadResponse || leadResponse.statusCode !== 200) {
            this.setState({
                loading: false,
                categories,
                loadError: leadResponse?.error || 'Failed to load this lead.',
            })
            return
        }

        const lead = leadResponse.data
        this.setState({
            loading: false,
            categories,
            fields: {
                category_id: lead.category?.id ? String(lead.category.id) : '',
                company_name: lead.company_name || '',
                company_linkedin_url: lead.company_linkedin_url || '',
                company_website_url: lead.company_website_url || '',
                company_email: lead.company_email || '',
                phone: lead.phone || '',
                google_place_id: lead.google_place_id || '',
                google_maps_url: lead.google_maps_url || '',
                company_description: lead.company_description || '',
                team_size_notes: lead.team_size_notes || '',
                website_notes: lead.website_notes || '',
                social_media_notes: lead.social_media_notes || '',
                contact_page_url: lead.contact_page_url || '',
                outreach_method: lead.outreach_method || '',
                outreach_done: !!lead.outreach_done,
                decision_maker_name: lead.decision_maker_name || '',
                decision_maker_title: lead.decision_maker_title || '',
                decision_maker_linkedin_url: lead.decision_maker_linkedin_url || '',
                decision_maker_email: lead.decision_maker_email || '',
                status: lead.status || 'new',
                priority: (lead.priority || lead.priority === 0) ? String(lead.priority) : '',
                notes: lead.notes || '',
            },
        })
    }

    setField = (field: keyof LeadFormFields, value: string | boolean) => {
        this.setState((prev: LeadFormPageState) => ({
            fields: { ...prev.fields, [field]: value }
        }))
    }

    handleTextChange = (field: keyof LeadFormFields) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setField(field, e.target.value)
    }

    handleSelectChange = (field: keyof LeadFormFields) => (e: ChangeEvent<HTMLSelectElement>) => {
        this.setField(field, e.target.value)
    }

    handleCancel = () => {
        navigator().navigate('/outreach')
    }

    handleSubmit = async (redirectToCompose: boolean = false) => {
        const { fields } = this.state

        if (!fields.company_name.trim()) {
            this.setState({ saveError: 'Company name is required.' })
            return
        }

        this.setState({ saving: true, saveError: null })

        const payload: any = {
            ...fields,
            category_id: fields.category_id ? Number(fields.category_id) : null,
            priority: fields.priority !== '' ? Number(fields.priority) : null,
        }

        const response = this.isEditMode
            ? await controllers().outreach.editLead(Number(this.props.leadId), payload)
            : await controllers().outreach.addLead(payload)

        if (!response || response.statusCode !== 200) {
            this.setState({ saving: false, saveError: response?.error || 'Failed to save lead.' })
            return
        }

        if (redirectToCompose) {
            const leadId = this.isEditMode ? Number(this.props.leadId) : response.data?.id
            navigator().navigate(leadId ? `/outreach/compose/${leadId}` : '/outreach/compose')
            return
        }

        navigator().navigate('/outreach')
    }

    render(): ReactNode {
        const { fields, categories, loading, needsAuth, loadError, saving, saveError } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">{this.isEditMode ? 'Edit Lead' : 'Add New Lead'}</h1>
                        <p className="outreach-subtitle">
                            {this.isEditMode ? 'Update this lead\'s details.' : 'Add a new company to your pipeline.'}
                        </p>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to manage leads.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && loadError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{loadError}</span>
                        <button className="outreach-banner-action" onClick={this.loadInitialData}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !needsAuth && !loadError && (
                    <div className="lead-form">
                        {saveError && <div className="category-form-error">{saveError}</div>}

                        <div className="lead-form-section">
                            <h3 className="lead-form-section-title">Company Info</h3>
                            <div className="lead-form-grid">
                                <div className="lead-form-field lead-form-field-wide">
                                    <label className="category-form-label">Company Name *</label>
                                    <input className="category-form-input" value={fields.company_name} onChange={this.handleTextChange('company_name')} placeholder="Acme Inc." />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Category</label>
                                    <select className="category-form-input" value={fields.category_id} onChange={this.handleSelectChange('category_id')}>
                                        <option value="">No category</option>
                                        {categories.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Website URL{fields.company_website_url && (
                                                <a
                                                    href={fields.company_website_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={fields.company_website_url}
                                                >
                                                    ↗
                                                </a>
                                            )}</label>
                                    <input className="category-form-input" value={fields.company_website_url} onChange={this.handleTextChange('company_website_url')} placeholder="https://" />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Company LinkedIn URL{fields.company_linkedin_url && (
                                                <a
                                                    href={fields.company_linkedin_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={fields.company_linkedin_url}
                                                >
                                                    ↗
                                                </a>
                                            )}</label>
                                    <input className="category-form-input" value={fields.company_linkedin_url} onChange={this.handleTextChange('company_linkedin_url')} placeholder="https://linkedin.com/company/..." />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Company Email</label>
                                    <input className="category-form-input" type="email" value={fields.company_email} onChange={this.handleTextChange('company_email')} placeholder="info@company.com" />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Phone</label>
                                    <input className="category-form-input" type="tel" value={fields.phone} onChange={this.handleTextChange('phone')} placeholder="+1 555 555 5555" />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Contact Page URL{fields.contact_page_url && (
                                                <a
                                                    href={fields.contact_page_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={fields.contact_page_url}
                                                >
                                                    ↗
                                                </a>
                                            )}</label>
                                    <input className="category-form-input" value={fields.contact_page_url} onChange={this.handleTextChange('contact_page_url')} placeholder="https://" />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Google Place ID</label>
                                    <input className="category-form-input" value={fields.google_place_id} onChange={this.handleTextChange('google_place_id')} />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Google Maps URL
                                        {fields.google_maps_url && (
                                                <a
                                                    href={fields.google_maps_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={fields.google_maps_url}
                                                >
                                                    ↗
                                                </a>
                                            )}</label>
                                    <input className="category-form-input" value={fields.google_maps_url} onChange={this.handleTextChange('google_maps_url')} placeholder="https://maps.google.com/..." />
                                </div>
                                <div className="lead-form-field lead-form-field-wide">
                                    <label className="category-form-label">Company Description</label>
                                    <textarea className="category-form-textarea" value={fields.company_description} onChange={this.handleTextChange('company_description')} placeholder="What the company does" rows={3} />
                                </div>
                            </div>
                        </div>

                        <div className="lead-form-section">
                            <h3 className="lead-form-section-title">Research Notes</h3>
                            <div className="lead-form-grid">
                                <div className="lead-form-field">
                                    <label className="category-form-label">Team Size Notes</label>
                                    <input className="category-form-input" value={fields.team_size_notes} onChange={this.handleTextChange('team_size_notes')} />
                                </div>
                                <div className="lead-form-field lead-form-field-wide">
                                    <label className="category-form-label">Website Notes</label>
                                    <textarea className="category-form-textarea" value={fields.website_notes} onChange={this.handleTextChange('website_notes')} placeholder="Site quality, AI features, etc." rows={2} />
                                </div>
                                <div className="lead-form-field lead-form-field-wide">
                                    <label className="category-form-label">Social Media Notes</label>
                                    <textarea className="category-form-textarea" value={fields.social_media_notes} onChange={this.handleTextChange('social_media_notes')} placeholder="How actively they run their social media" rows={2} />
                                </div>
                            </div>
                        </div>

                        <div className="lead-form-section">
                            <h3 className="lead-form-section-title">Outreach</h3>
                            <div className="lead-form-grid">
                                <div className="lead-form-field">
                                    <label className="category-form-label">Outreach Method</label>
                                    <select className="category-form-input" value={fields.outreach_method} onChange={this.handleSelectChange('outreach_method')}>
                                        {OUTREACH_METHOD_OPTIONS.map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="lead-form-field lead-form-checkbox-field">
                                    <label className="category-form-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={fields.outreach_done}
                                            onChange={(e) => this.setField('outreach_done', e.target.checked)}
                                        />
                                        Outreach sent
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="lead-form-section">
                            <h3 className="lead-form-section-title">Decision Maker</h3>
                            <div className="lead-form-grid">
                                <div className="lead-form-field">
                                    <label className="category-form-label">Name</label>
                                    <input className="category-form-input" value={fields.decision_maker_name} onChange={this.handleTextChange('decision_maker_name')} />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Title</label>
                                    <input className="category-form-input" value={fields.decision_maker_title} onChange={this.handleTextChange('decision_maker_title')} />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Email</label>
                                    <input className="category-form-input" type="email" value={fields.decision_maker_email} onChange={this.handleTextChange('decision_maker_email')} />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">LinkedIn URL{fields.decision_maker_linkedin_url && (
                                                <a
                                                    href={fields.decision_maker_linkedin_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="outreach-external-link-icon"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={fields.decision_maker_linkedin_url}
                                                >
                                                    ↗
                                                </a>
                                            )}</label>
                                    <input className="category-form-input" value={fields.decision_maker_linkedin_url} onChange={this.handleTextChange('decision_maker_linkedin_url')} placeholder="https://linkedin.com/in/..." />
                                </div>
                            </div>
                        </div>

                        <div className="lead-form-section">
                            <h3 className="lead-form-section-title">Pipeline</h3>
                            <div className="lead-form-grid">
                                <div className="lead-form-field">
                                    <label className="category-form-label">Status</label>
                                    <select className="category-form-input" value={fields.status} onChange={this.handleSelectChange('status')}>
                                        {LEAD_STATUS_OPTIONS.map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Priority</label>
                                    <input
                                        className="category-form-input"
                                        type="number"
                                        value={fields.priority}
                                        onChange={this.handleTextChange('priority')}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="lead-form-field lead-form-field-wide">
                                    <label className="category-form-label">Notes</label>
                                    <textarea className="category-form-textarea" value={fields.notes} onChange={this.handleTextChange('notes')} rows={3} />
                                </div>
                            </div>
                        </div>

                        <div className="lead-form-actions">
                            <button className="outreach-modal-cancel" onClick={this.handleCancel} disabled={saving}>
                                Cancel
                            </button>
                            <button className="outreach-add-btn lead-form-compose-btn" onClick={() => this.handleSubmit(true)} disabled={saving}>
                                {saving ? 'Saving...' : 'Save & Start Sending Email'}
                            </button>
                            <button className="outreach-add-btn" onClick={() => this.handleSubmit(false)} disabled={saving}>
                                {saving ? 'Saving...' : (this.isEditMode ? 'Save Changes' : 'Create Lead')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

export { LeadFormPage }
